import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, linkedSignal, output, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { s25Resource } from "../../../util/s25.resource";
import { S25Util } from "../../../util/s25-util";
import { AlertService } from "../../../services/alert.service";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { S25DndSortableModule } from "../../s25-dnd-sortable/s25.dnd.sortable.module";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { TrailApi, type FocusAreaDto } from "./trail.api";
import { TrailLabels } from "./trail.labels";
import {
    changedSortOrders,
    createSortOrderSaveQueue,
    nextAppendSortOrder,
    SORT_ORDER_PERSIST_DEBOUNCE_MS,
} from "../../../util/sort.order.util";

@TypeManagerDecorator("s25-ng-trail-focus-area-admin")
@Component({
    selector: "s25-ng-trail-focus-area-admin",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, S25ButtonComponent, S25DndSortableModule, S25IconComponent],
    host: { class: "trail-focus-area-admin" },
    template: `
        <div class="c-padding--double">
            <div class="trail-focus-area-admin__header c-margin-bottom--single">
                <h2>{{ labels.FOCUS_AREA }} Management</h2>
                <s25-ng-button [type]="'outline'" [onClick]="handleBack">← Back</s25-ng-button>
            </div>

            @if (focusAreaList.isLoading()) {
                <p>Loading…</p>
            } @else if (focusAreaList.error()) {
                <p class="c-text--error">{{ formatError(focusAreaList.error()) }}</p>
            } @else {
                <div class="trail-focus-area-admin__content">
                    <section class="trail-focus-area-admin__list-section">
                        <div class="trail-focus-area-admin__list-heading">
                            <h3>{{ labels.FOCUS_AREAS }}</h3>
                            <s25-ng-button data-id="refresh-list" [type]="'outline'" [onClick]="refreshList">
                                Refresh
                            </s25-ng-button>
                        </div>
                        @if (!sortedFocusAreas().length) {
                            <p class="c-text--secondary">No focus areas defined yet.</p>
                        } @else {
                            <table class="c-table">
                                <thead>
                                    <tr>
                                        <th class="trail-focus-area-admin__drag-col"></th>
                                        <th>Name</th>
                                        <th>Key</th>
                                        <th>Description</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody
                                    s25-ng-dnd-sortable
                                    [items]="sortableFocusAreas()"
                                    (orderChanged)="onFocusAreaOrderChanged($event)"
                                >
                                    @for (
                                        area of sortableFocusAreas();
                                        track area.focusAreaId;
                                        let areaIndex = $index
                                    ) {
                                        <tr s25-ng-dnd-sortable-item [index]="areaIndex" [draggable]="true">
                                            <td class="trail-focus-area-admin__drag-col">
                                                <s25-ng-icon [type]="'dragHandle'" [ariaLabel]="'Reorder focus area'" />
                                            </td>
                                            <td>{{ area.name }}</td>
                                            <td>{{ area.focusAreaKey }}</td>
                                            <td>{{ area.description ?? "—" }}</td>
                                            <td>
                                                <s25-ng-button [type]="'outline'" [onClick]="editFactory(area)">
                                                    Edit
                                                </s25-ng-button>
                                            </td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        }
                        <p class="c-margin-top--single">
                            <s25-ng-button [type]="'primary'" [onClick]="startCreate">
                                New {{ labels.FOCUS_AREA }}
                            </s25-ng-button>
                        </p>
                    </section>

                    @if (showForm()) {
                        <section
                            class="trail-focus-area-admin__form-section c-border c-padding--double c-margin-top--double"
                        >
                            <h3>{{ formFocusAreaId() ? "Edit" : "Create" }} {{ labels.FOCUS_AREA }}</h3>
                            <div class="form-group c-margin-bottom--single">
                                <label>Key *</label>
                                <input
                                    class="c-input"
                                    [ngModel]="formKey()"
                                    (ngModelChange)="formKey.set($event)"
                                    placeholder="e.g. leadership"
                                    required
                                />
                            </div>
                            <div class="form-group c-margin-bottom--single">
                                <label>Name *</label>
                                <input
                                    class="c-input"
                                    [ngModel]="formName()"
                                    (ngModelChange)="formName.set($event)"
                                    placeholder="e.g. Leadership"
                                    required
                                />
                            </div>
                            <div class="form-group c-margin-bottom--single">
                                <label>Description</label>
                                <input
                                    class="c-input"
                                    [ngModel]="formDescription()"
                                    (ngModelChange)="formDescription.set($event)"
                                    placeholder="Optional description"
                                />
                            </div>
                            @if (formError()) {
                                <p class="c-text--error c-margin-bottom--single">{{ formError() }}</p>
                            }
                            <div class="trail-focus-area-admin__form-actions">
                                <s25-ng-button [type]="'primary'" [onClick]="saveFocusArea">Save</s25-ng-button>
                                <s25-ng-button [type]="'danger--outline'" [onClick]="cancelForm">Cancel</s25-ng-button>
                            </div>
                        </section>
                    }
                </div>
            }
        </div>
    `,
    styles: `
        .trail-focus-area-admin__header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 1rem;
        }
        .trail-focus-area-admin__list-heading {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            flex-wrap: wrap;
            margin-bottom: 0.75rem;
        }
        .trail-focus-area-admin__list-heading h3 {
            margin: 0;
        }
        .c-table {
            width: 100%;
            border-collapse: collapse;
        }
        .c-table th,
        .c-table td {
            border: 1px solid var(--color--border, #ddd);
            padding: 0.4rem 0.6rem;
            text-align: left;
        }
        .trail-focus-area-admin__form-section .form-group {
            display: grid;
            grid-template-columns: 12rem minmax(0, 1fr);
            gap: 0.5rem;
            align-items: center;
            max-width: 36rem;
        }
        .trail-focus-area-admin__form-actions {
            display: flex;
            gap: 0.75rem;
            margin-top: 0.5rem;
        }
        .trail-focus-area-admin__drag-col {
            width: 1.75rem;
            text-align: center;
        }
        .trail-focus-area-admin__list-section tbody.sortable [s25-ng-dnd-sortable-item] {
            cursor: grab;
        }
    `,
})
export class TrailFocusAreaAdminComponent {
    readonly labels = TrailLabels;
    readonly backToAdmin = output<void>();

    handleBack = () => {
        this.backToAdmin.emit();
    };

    private readonly listGen = signal(0);

    refreshList = () => {
        this.listGen.update((count) => count + 1);
    };

    readonly focusAreaList = s25Resource({
        params: () => this.listGen(),
        loader: () => TrailApi.getFocusAreas(),
    });

    readonly sortedFocusAreas = computed((): FocusAreaDto[] => {
        const areas = this.focusAreaList.value() ?? [];
        return [...areas].sort((areaA, areaB) => {
            const orderA = areaA.sortOrder ?? Number.MAX_SAFE_INTEGER;
            const orderB = areaB.sortOrder ?? Number.MAX_SAFE_INTEGER;
            if (orderA !== orderB) return orderA - orderB;
            return (areaA.name ?? "").localeCompare(areaB.name ?? "", undefined, { sensitivity: "base" });
        });
    });

    readonly sortableFocusAreas = linkedSignal((): FocusAreaDto[] => this.sortedFocusAreas());
    private focusAreaOrderPersistHandle: ReturnType<typeof setTimeout> | null = null;
    private readonly enqueueFocusAreaOrderSave = createSortOrderSaveQueue();

    readonly showForm = signal(false);
    readonly formFocusAreaId = signal<number | null>(null);
    readonly formKey = signal("");
    readonly formName = signal("");
    readonly formDescription = signal("");
    readonly formSortOrder = signal<number | null>(null);
    readonly formError = signal<string | null>(null);

    formatError(error: unknown): string {
        if (error instanceof Error) return error.message;
        return String(error ?? "Unknown error");
    }

    startCreate = () => {
        this.formFocusAreaId.set(null);
        this.formKey.set("");
        this.formName.set("");
        this.formDescription.set("");
        this.formSortOrder.set(nextAppendSortOrder(this.sortedFocusAreas().map((area) => area.sortOrder)));
        this.formError.set(null);
        this.showForm.set(true);
    };

    editFactory = (area: FocusAreaDto) => {
        return () => {
            this.formFocusAreaId.set(area.focusAreaId ?? null);
            this.formKey.set(area.focusAreaKey);
            this.formName.set(area.name);
            this.formDescription.set(area.description ?? "");
            this.formSortOrder.set(area.sortOrder ?? null);
            this.formError.set(null);
            this.showForm.set(true);
        };
    };

    cancelForm = () => {
        this.showForm.set(false);
        this.formError.set(null);
    };

    onFocusAreaOrderChanged(areas: FocusAreaDto[]): void {
        this.sortableFocusAreas.set(areas);
        this.queueFocusAreaOrderPersist();
    }

    private queueFocusAreaOrderPersist(): void {
        if (this.focusAreaOrderPersistHandle != null) {
            clearTimeout(this.focusAreaOrderPersistHandle);
        }
        this.focusAreaOrderPersistHandle = setTimeout(() => {
            this.focusAreaOrderPersistHandle = null;
            void this.enqueueFocusAreaOrderSave(() => this.persistFocusAreaOrder());
        }, SORT_ORDER_PERSIST_DEBOUNCE_MS);
    }

    private async persistFocusAreaOrder(): Promise<void> {
        const areas = this.sortableFocusAreas();
        const { orders, changes } = changedSortOrders(
            areas,
            (area) => area.focusAreaId,
            (area) => area.sortOrder,
        );
        if (!changes.length) {
            return;
        }
        const updated = areas.map((area, areaIndex) => ({ ...area, sortOrder: orders[areaIndex] }));
        this.sortableFocusAreas.set(updated);
        try {
            await TrailApi.updateFocusAreaSortOrders(
                changes.map((change) => ({ focusAreaId: change.id, sortOrder: change.sortOrder })),
            );
        } catch (caughtError) {
            if (this.sortableFocusAreas() === updated) {
                this.sortableFocusAreas.set(areas);
            }
            await AlertService.alert(S25Util.errorText(caughtError));
        }
    }

    saveFocusArea = async () => {
        const key = this.formKey().trim();
        const name = this.formName().trim();
        if (!key || !name) {
            this.formError.set("Key and Name are required.");
            return;
        }
        const payload: FocusAreaDto = {
            focusAreaId: this.formFocusAreaId() ?? undefined,
            focusAreaKey: key,
            name,
            description: this.formDescription().trim() || undefined,
            sortOrder: this.formSortOrder() ?? undefined,
        };
        try {
            const result = await TrailApi.saveFocusArea(payload);
            if (result?.err_code) {
                this.formError.set(result.err_msg ?? "A focus area with this key already exists.");
                return;
            }
            this.showForm.set(false);
            this.formError.set(null);
            this.listGen.update((count) => count + 1);
        } catch (caughtError) {
            this.formError.set(S25Util.errorText(caughtError) ?? "Save failed.");
        }
    };
}
